import React, { useState, useEffect } from 'react'
import { fetchCallRecording, syncCallFromExotel, getRecordingStreamUrl } from '../../services/telephonyApi'

/**
 * CallAuditDetailDrawer Component
 * Slide-over drawer modal rendering detailed call SID audit record, customer/project banner, linked ERP Job & Task, notes, and audio playback.
 */
export default function CallAuditDetailDrawer({ selectedLog, onClose, formatDuration }) {
  const [recUrl, setRecUrl] = useState(selectedLog?.recording_url || '')
  const [loadingRec, setLoadingRec] = useState(false)
  const [syncingExotel, setSyncingExotel] = useState(false)
  const [currentStatus, setCurrentStatus] = useState(selectedLog?.call_status || '')
  const [currentDuration, setCurrentDuration] = useState(selectedLog?.duration_seconds || 0)

  useEffect(() => {
    setRecUrl(selectedLog?.recording_url || '')
    setCurrentStatus(selectedLog?.call_status || '')
    setCurrentDuration(selectedLog?.duration_seconds || 0)
  }, [selectedLog])

  if (!selectedLog) return null

  const handleFetchRecording = async () => {
    const sid = selectedLog.call_sid || selectedLog.exotel_call_sid
    if (!sid) return
    setLoadingRec(true)
    try {
      const res = await fetchCallRecording(sid)
      if (res?.recording_url) {
        setRecUrl(res.recording_url)
      }
    } catch (err) {
      console.warn('Failed to fetch recording:', err)
    } finally {
      setLoadingRec(false)
    }
  }

  const handleSyncExotel = async () => {
    const sid = selectedLog.call_sid || selectedLog.exotel_call_sid
    if (!sid) return
    setSyncingExotel(true)
    try {
      const res = await syncCallFromExotel(sid)
      if (res?.success && res.data) {
        if (res.data.recording_url) setRecUrl(res.data.recording_url)
        if (res.data.status) setCurrentStatus(res.data.status)
        if (typeof res.data.duration_seconds === 'number') setCurrentDuration(res.data.duration_seconds)
      }
    } catch (err) {
      console.warn('Failed to sync call with Exotel API:', err)
    } finally {
      setSyncingExotel(false)
    }
  }

  const routingHistory = React.useMemo(() => {
    let hist = selectedLog?.routing_history
    if (typeof hist === 'string') {
      try {
        hist = JSON.parse(hist)
      } catch (e) {
        hist = []
      }
    }
    return Array.isArray(hist) ? hist : []
  }, [selectedLog?.routing_history])

  const sidDisplay = selectedLog.call_sid || selectedLog.exotel_call_sid || `ID #${selectedLog.id}`
  const notesDisplay = selectedLog.call_notes || selectedLog.notes || 'No call notes recorded.'
  const st = (currentStatus || '').toUpperCase()

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-xs transition-opacity animate-fade-in">
      <div className="w-full max-w-lg bg-surface-container-lowest h-full shadow-2xl flex flex-col justify-between overflow-y-auto">
        {/* Drawer Header */}
        <div className="p-5 border-b border-outline-variant/30 flex items-center justify-between bg-surface-container-low">
          <div>
            <h3 className="text-base font-bold font-display text-on-surface">Call Audit Record</h3>
            <p className="text-xs font-mono text-on-surface-variant">{sidDisplay}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={handleSyncExotel}
              disabled={syncingExotel}
              title="Sync latest details from Exotel REST API"
              className="p-2 rounded-full hover:bg-surface-container-highest text-primary transition-colors disabled:opacity-50"
            >
              <span className={`material-symbols-outlined text-lg ${syncingExotel ? 'animate-spin' : ''}`}>
                sync
              </span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-full hover:bg-surface-container-highest text-on-surface-variant transition-colors"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>

        {/* Drawer Body */}
        <div className="p-6 space-y-6 flex-1">
          {/* Customer & Project Banner Card */}
          <div className="p-4 rounded-2xl bg-surface-container-low border border-outline-variant/30 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-on-surface">{selectedLog.caller_name || selectedLog.customer_name}</h4>
              <div className="flex items-center gap-2">
                <span
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                    (selectedLog.direction || '').toUpperCase() === 'INBOUND'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {selectedLog.direction}
                </span>

                {/* Status Badge in Banner */}
                {st === 'COMPLETED' && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] font-bold">
                    Completed
                  </span>
                )}
                {st === 'MISSED' && (
                  <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-200 text-[10px] font-bold">
                    Missed Call
                  </span>
                )}
                {st === 'BUSY' && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-300 text-[10px] font-bold">
                    {selectedLog.direction === 'INBOUND' ? 'Agent Declined' : 'Busy'}
                  </span>
                )}
                {['CANCELED', 'CANCELLED'].includes(st) && (
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-300 text-[10px] font-bold">
                    Dropped
                  </span>
                )}
                {['NO-ANSWER', 'NO_ANSWER'].includes(st) && (
                  <span className="px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-800 border border-yellow-300 text-[10px] font-bold">
                    No Answer
                  </span>
                )}
                {st === 'FAILED' && (
                  <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 text-[10px] font-bold">
                    Failed
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs font-mono text-on-surface-variant">{selectedLog.caller_number}</p>
            <div className="pt-2 border-t border-outline-variant/20 grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-[10px] uppercase font-bold text-outline block">Customer / Project</span>
                <span className="font-semibold text-on-surface">{selectedLog.customer_name || selectedLog.project_name || 'Unassigned'}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-outline block">Timesheet Status</span>
                <span className="font-semibold text-amber-700">{selectedLog.timesheet_status || (selectedLog.timesheet_id ? 'PENDING' : 'None')}</span>
              </div>
            </div>
          </div>

          {/* Call Details Grid */}
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="p-3 rounded-xl bg-surface-container-low/40 border border-outline-variant/20">
              <span className="text-[10px] uppercase font-bold text-outline block">Assigned Agent</span>
              <span className="font-bold text-on-surface">{selectedLog.agent_name || 'Queue / Unassigned'}</span>
              {selectedLog.agent_email && <span className="text-[11px] text-on-surface-variant block">{selectedLog.agent_email}</span>}
            </div>

            <div className="p-3 rounded-xl bg-surface-container-low/40 border border-outline-variant/20">
              <span className="text-[10px] uppercase font-bold text-outline block">Talk Duration</span>
              <span className="font-bold text-on-surface">{formatDuration(currentDuration)}</span>
              <span className="text-[11px] text-on-surface-variant block">{selectedLog.start_time || selectedLog.created_at}</span>
            </div>
          </div>

          {/* Agent Routing Timeline */}
          {routingHistory.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-sm text-primary">alt_route</span>
                  Agent Routing Timeline
                </span>
                <span className="text-[10px] text-on-surface-variant font-medium">
                  {routingHistory.length} {routingHistory.length === 1 ? 'attempt' : 'attempts'}
                </span>
              </div>
              <div className="p-3.5 rounded-2xl bg-surface-container-low border border-outline-variant/30 divide-y divide-outline-variant/20">
                {routingHistory.map((step, idx) => {
                  const status = (step.status || '').toUpperCase()
                  const isAnswered = status === 'ANSWERED' || status === 'COMPLETED'
                  const isMissed = status === 'MISSED'
                  const isDeclined = status === 'DECLINED' || status === 'BUSY'
                  const isRinging = status === 'RINGING'
                  const isSkipped = status === 'SKIPPED'
                  const isOffline = status === 'OFFLINE'

                  let badgeColor = 'bg-slate-100 text-slate-700 border-slate-300'
                  let icon = 'help_outline'
                  let iconColor = 'text-slate-500'

                  if (isAnswered) {
                    badgeColor = 'bg-emerald-50 text-emerald-800 border-emerald-200'
                    icon = 'check_circle'
                    iconColor = 'text-emerald-600'
                  } else if (isMissed) {
                    badgeColor = 'bg-rose-50 text-rose-800 border-rose-200'
                    icon = 'phone_missed'
                    iconColor = 'text-rose-600'
                  } else if (isDeclined) {
                    badgeColor = 'bg-amber-50 text-amber-900 border-amber-300'
                    icon = 'do_not_disturb_on'
                    iconColor = 'text-amber-600'
                  } else if (isRinging) {
                    badgeColor = 'bg-blue-50 text-blue-800 border-blue-200'
                    icon = 'ring_volume'
                    iconColor = 'text-blue-600'
                  } else if (isSkipped) {
                    badgeColor = 'bg-slate-100 text-slate-700 border-slate-300'
                    icon = 'fast_forward'
                    iconColor = 'text-slate-500'
                  } else if (isOffline) {
                    badgeColor = 'bg-zinc-100 text-zinc-600 border-zinc-300'
                    icon = 'cloud_off'
                    iconColor = 'text-zinc-500'
                  }

                  return (
                    <div key={idx} className={`flex items-start gap-3 py-2.5 ${idx === 0 ? 'pt-0' : ''} ${idx === routingHistory.length - 1 ? 'pb-0' : ''}`}>
                      <div className="mt-0.5">
                        <span className={`material-symbols-outlined text-base ${iconColor} ${isRinging ? 'animate-pulse' : ''}`}>
                          {icon}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0 space-y-0.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-on-surface truncate">
                            {step.agent_name || `Agent ${step.user_id || idx + 1}`}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${badgeColor}`}>
                            {status}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-on-surface-variant">
                          {step.ring_duration > 0 && <span>Rang: {step.ring_duration}s</span>}
                          {step.talk_duration > 0 && <span>Talk: {formatDuration(step.talk_duration)}</span>}
                          {step.reason && <span className="italic text-[10px] text-outline">{step.reason}</span>}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Linked ERP Job & Task or Unanswered Notice */}
          {selectedLog.timesheet_id ? (
            <div className="p-4 rounded-2xl border border-blue-200 bg-blue-50/50 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-blue-900">Linked Auto-Timesheet</span>
                <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-bold">
                  {selectedLog.timesheet_status || 'PENDING'}
                </span>
              </div>
              <p className="text-[11px] text-blue-700">
                Auto-created daily timesheet record #{selectedLog.timesheet_id} awaiting direct manager approval.
              </p>
            </div>
          ) : currentDuration === 0 ? (
            <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50/70 flex items-start gap-2.5">
              <span className="material-symbols-outlined text-slate-500 text-base mt-0.5">info</span>
              <p className="text-xs text-slate-600 leading-relaxed">
                No timesheet was generated for this call because it was not answered (Talk Duration: 0s).
              </p>
            </div>
          ) : null}

          {/* Call Disposition Notes */}
          <div className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-on-surface-variant block">
              Call Disposition Notes
            </span>
            <div className="p-4 rounded-2xl bg-surface-container-low border border-outline-variant/30 text-xs text-on-surface leading-relaxed italic">
              "{notesDisplay}"
            </div>
          </div>

          {/* Audio Playback Controls */}
          <div className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-on-surface-variant block">
              Voice Recording Playback
            </span>
            {recUrl && (selectedLog.call_sid || selectedLog.exotel_call_sid) ? (
              <div className="p-4 rounded-2xl bg-surface-container-low border border-outline-variant/30 space-y-3">
                <audio
                  controls
                  src={getRecordingStreamUrl(selectedLog.call_sid || selectedLog.exotel_call_sid)}
                  className="w-full rounded-lg"
                  preload="metadata"
                />
              </div>
            ) : (
              <div className="p-4 rounded-2xl bg-surface-container-low border border-outline-variant/30 flex items-center justify-between">
                <span className="text-xs text-on-surface-variant">Recording not yet linked or still rendering.</span>
                <button
                  type="button"
                  onClick={handleFetchRecording}
                  disabled={loadingRec}
                  className="px-3 py-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary font-bold text-xs flex items-center gap-1.5 transition-colors disabled:opacity-50"
                >
                  <span className={`material-symbols-outlined text-sm ${loadingRec ? 'animate-spin' : ''}`}>
                    {loadingRec ? 'progress_activity' : 'refresh'}
                  </span>
                  <span>{loadingRec ? 'Checking...' : 'Fetch Recording'}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Drawer Footer */}
        <div className="p-4 bg-surface-container-low border-t border-outline-variant/30 flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-on-primary font-bold text-xs shadow-md transition-colors"
          >
            Close Audit Record
          </button>
        </div>
      </div>
    </div>
  )
}
